/** Normalização de tipos de mensagem e camadas da timeline. */

import { CHANNEL_TYPES } from "../../constants/channelCatalog";
import { resolveMessageApiAudit } from "../../utils/partnerApiAuditDisplay";
import { looksLikeAiAgentLogText, resolveMessageAiAudit } from "../../utils/aiAgentAuditDisplay";
import { isPartsQuoteRequestMessage } from "../../utils/parsePartsQuoteRequest";

export { isPartsQuoteRequestMessage };

export const TIMELINE_LAYERS = {
  CONVERSATION: "conversation",
  INTERNAL: "internal",
  ACTIVITY: "activity",
  ACCESS: "access",
};

/**
 * Camadas principais + refinamentos da conversa + logs técnicos (master).
 * Refinamentos (media/email/bot/survey) estreitam a camada "conversa" quando
 * combinados com ela; sozinhos, atuam como subconjunto da conversa.
 */
export const TIMELINE_FILTERS = {
  ALL: "all",
  CONVERSATION: "conversation",
  INTERNAL: "internal",
  ACTIVITY: "activity",
  ACCESS: "access",
  MEDIA: "media",
  EMAIL: "email",
  BOT: "bot",
  SURVEY: "survey",
  API_LOGS: "api_logs",
  AI_LOGS: "ai_logs",
};

export const TIMELINE_LAYER_FILTERS = [
  TIMELINE_FILTERS.CONVERSATION,
  TIMELINE_FILTERS.INTERNAL,
  TIMELINE_FILTERS.ACTIVITY,
  TIMELINE_FILTERS.ACCESS,
];

export const TIMELINE_CONVERSATION_FACETS = [
  TIMELINE_FILTERS.MEDIA,
  TIMELINE_FILTERS.EMAIL,
  TIMELINE_FILTERS.BOT,
  TIMELINE_FILTERS.SURVEY,
];

export const MASTER_TIMELINE_FILTERS = [TIMELINE_FILTERS.API_LOGS, TIMELINE_FILTERS.AI_LOGS];

/** Todos os filtros toggleáveis (exceto master). */
export const TOGGLEABLE_TIMELINE_FILTERS = [
  ...TIMELINE_LAYER_FILTERS,
  ...TIMELINE_CONVERSATION_FACETS,
];

const MEDIA_TYPES = new Set([
  "image",
  "video",
  "audio",
  "file",
  "document",
  "sticker",
  "location",
  "contact",
]);

const BOT_TYPES = new Set(["automaticanswer", "auto_response", "scheduled"]);

const SURVEY_TYPES = new Set(["survey"]);

export const isInternalNoteType = (message) => {
  if (isPartsQuoteRequestMessage(message)) return false;
  const t = (message?.type || "").toLowerCase();
  return t === "note" || t === "xpro";
};

export const isLogType = (message) => (message?.type || "").toLowerCase() === "log";

export const LEAD_VIEW_LOG_CODE = "lead_view";

/** Auditoria LGPD: quem abriu a solicitação. Fora da camada Atividade e oculta por padrão. */
export const isLeadAccessLog = (message) => {
  if (!message) return false;
  if (message?.logMeta?.code === LEAD_VIEW_LOG_CODE) return true;
  if (String(message?.id || "").startsWith("access-")) return true;
  return false;
};

export function looksLikePartnerApiLogText(text) {
  if (text == null) return false;
  const plain =
    typeof text === "string" && text.includes("<")
      ? text.replace(/<[^>]*>/g, "").trim()
      : String(text).trim();
  if (!plain) return false;
  return /^(?:API\s+)?(Gupshup|TotalBus|Praxio|Praxxio|Vuxx|Cielo|Rede|MercadoPago|Itaú|Itau|AppMax|Xpay|Pagamento|Twilio|Telefonia)\b/i.test(plain);
}

export const isPartnerApiLogMessage = (message) => {
  if (!message) return false;
  // Só oculta linhas de log dedicadas (tipo 7 / activity). Mensagens reais do cliente ou
  // do atendimento podem carregar xpro_api_audit no json_whatsapp — não são "log de API".
  if ((message?.type || "").toLowerCase() !== "log") return false;
  const audit = resolveMessageApiAudit(message);
  if (audit && (audit.kind === "partner_api_audit" || audit.partner)) return true;
  return looksLikePartnerApiLogText(message?.textPlain || message?.text || "");
};

export const isAiAgentLogMessage = (message) => {
  if (!message) return false;
  if ((message?.type || "").toLowerCase() !== "log") return false;
  const audit = resolveMessageAiAudit(message);
  if (audit && audit.kind === "ai_agent_audit") return true;
  return looksLikeAiAgentLogText(message?.textPlain || message?.text || "");
};

export const isMasterTechnicalLogMessage = (message) =>
  isPartnerApiLogMessage(message) || isAiAgentLogMessage(message);

/** Transcrição duplicada: já aparece em “Ver transcrição” no card da gravação. */
export const isVoiceCallTranscriptMessage = (message) => {
  if (!message) return false;
  if ((message?.type || "").toLowerCase() !== "log") return false;
  if (message?.logMeta?.code === "voice_call_transcript") return true;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    if (j?.xpro_voice_call_transcript) return true;
    if (j?.xpro_log?.code === "voice_call_transcript") return true;
  } catch {
    /* ignore */
  }
  const text = String(message?.textPlain || message?.text || "");
  return /^\s*Transcrição da ligação\b/i.test(text);
};

/** Log redundante “Gravação disponível” — o card da chamada já mostra a gravação. */
export const isVideoRecordingReadyLogMessage = (message) => {
  if (!message) return false;
  if ((message?.type || "").toLowerCase() !== "log") return false;
  if (message?.logMeta?.code === "video_recording_ready") return true;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    if (j?.xpro_log?.code === "video_recording_ready") return true;
  } catch {
    /* ignore */
  }
  const text = String(message?.textPlain || message?.text || "");
  return /^\s*Gravação da chamada disponível\b/i.test(text);
};

/** Log “Chamada de vídeo encerrada” — redundante quando já existe o card de resumo. */
export const isVideoCallEndedLogMessage = (message) => {
  if (!message) return false;
  if ((message?.type || "").toLowerCase() !== "log") return false;
  if (message?.logMeta?.code === "video_call_ended") return true;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    if (j?.xpro_log?.code === "video_call_ended") return true;
  } catch {
    /* ignore */
  }
  const text = String(message?.textPlain || message?.text || "").trim();
  return /^Chamada de vídeo encerrada\b/i.test(text);
};

export function isRedundantVideoCallEndedLog(message, allMessages = []) {
  if (!isVideoCallEndedLogMessage(message)) return false;
  const list = Array.isArray(allMessages) ? allMessages : [];
  return list.some((m) => {
    if (!m || String(m.id) === String(message.id)) return false;
    try {
      const raw = m?.json_whatsapp;
      const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
      return Boolean(j?.xpro_video_call_summary);
    } catch {
      return false;
    }
  });
}

/** Anexo tipo 15 da composition Twilio — preferir o player no card de resumo. */
export const isXproVideoRecordingAttachment = (message) => {
  if (!message) return false;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    return Boolean(j?.xpro_video_recording);
  } catch {
    return false;
  }
};

export function getXproVideoRecordingUrl(message) {
  if (!message) return "";
  const direct = String(message.video || message.url || "").trim();
  if (direct) return direct;
  try {
    const raw = message?.json_whatsapp;
    const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
    return String(j?.xpro_video_recording?.url || "").trim();
  } catch {
    return "";
  }
}

/** true se a timeline já tem resumo com a mesma gravação (evita bolha + card). */
export function isRedundantXproVideoRecordingAttachment(message, allMessages = []) {
  if (!isXproVideoRecordingAttachment(message)) return false;
  const url = getXproVideoRecordingUrl(message);
  if (!url) return true;
  const list = Array.isArray(allMessages) ? allMessages : [];
  return list.some((m) => {
    if (!m || String(m.id) === String(message.id)) return false;
    try {
      const raw = m?.json_whatsapp;
      const j = raw == null ? null : typeof raw === "string" ? JSON.parse(raw) : raw;
      const summaryUrl = String(j?.xpro_video_call_summary?.recordingUrl || "").trim();
      return Boolean(summaryUrl) && summaryUrl === url;
    } catch {
      return false;
    }
  });
}

/** Logs operacionais (transferência, status, etc.) — exclui audit técnico de master e acessos LGPD. */
export const isOperationalActivityLog = (message) =>
  isLogType(message) && !isMasterTechnicalLogMessage(message) && !isLeadAccessLog(message);

export const getTimelineLayer = (message) => {
  if (!message) return TIMELINE_LAYERS.CONVERSATION;
  if (isLeadAccessLog(message)) return TIMELINE_LAYERS.ACCESS;
  if (isLogType(message)) return TIMELINE_LAYERS.ACTIVITY;
  if (isPartsQuoteRequestMessage(message)) return TIMELINE_LAYERS.CONVERSATION;
  if (isInternalNoteType(message)) return TIMELINE_LAYERS.INTERNAL;
  return TIMELINE_LAYERS.CONVERSATION;
};

export const isClientMessage = (message) => {
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();
  return (
    sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    message?.channel === "customer"
  );
};

export const isConversationMessage = (message) =>
  getTimelineLayer(message) === TIMELINE_LAYERS.CONVERSATION;

export const isMediaMessage = (message) => {
  if (!message || !isConversationMessage(message)) return false;
  const t = (message?.type || "").toLowerCase();
  if (MEDIA_TYPES.has(t)) return true;
  if (message?.image || message?.video || message?.audio) return true;
  if (message?.document?.url || message?.location?.latitude != null) return true;
  if (message?.contact?.phone || message?.contact?.whatsappId) return true;
  return false;
};

export const isEmailMessage = (message) => {
  if (!message || !isConversationMessage(message)) return false;
  const channel = String(message?.channel || "");
  const channelName = String(message?.channelName || "");
  const rawType = String(message?.rawChannelType || message?.channelType || "");
  if (/e-?mail/i.test(channel) || /e-?mail/i.test(channelName) || /e-?mail/i.test(rawType)) {
    return true;
  }
  if (message?.emailOriginal || message?.emailSubject || message?.emailFrom || message?.hasEmailOriginal) {
    return true;
  }
  if (Array.isArray(message?.emailAttachments) && message.emailAttachments.length > 0) return true;
  if (message?.status_email != null && String(message.status_email).trim() !== "") return true;
  return false;
};

/** Respostas automáticas / chatbot / agente de IA na conversa com o cliente (não audit master). */
export const isBotCustomerMessage = (message) => {
  if (!message || !isConversationMessage(message)) return false;
  const t = (message?.type || "").toLowerCase();
  if (BOT_TYPES.has(t)) return true;
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();
  if (
    senderType === "chatbot" ||
    senderType === "ai_agent" ||
    sender === "chatbot" ||
    sender === "ai_agent"
  ) {
    return true;
  }
  return Boolean(message?.isChatbotSimulation);
};

/** Pesquisas enviadas/respondidas (mensagem de survey ou log de resposta). */
export const isSurveyMessage = (message) => {
  if (!message) return false;
  const t = (message?.type || "").toLowerCase();
  if (SURVEY_TYPES.has(t)) return true;
  if (message?.logMeta?.code === "survey_response") return true;
  const channel = String(message?.channel || message?.channelName || "");
  if (/survey|pesquisa/i.test(channel) && isConversationMessage(message)) return true;
  return false;
};

export const isTimelineLayerFilter = (filter) =>
  TIMELINE_LAYER_FILTERS.includes(filter) || MASTER_TIMELINE_FILTERS.includes(filter);

export const isTimelineConversationFacet = (filter) =>
  TIMELINE_CONVERSATION_FACETS.includes(filter);

export const matchesConversationFacet = (message, filter) => {
  if (!message) return false;
  if (filter === TIMELINE_FILTERS.MEDIA) return isMediaMessage(message);
  if (filter === TIMELINE_FILTERS.EMAIL) return isEmailMessage(message);
  if (filter === TIMELINE_FILTERS.BOT) return isBotCustomerMessage(message);
  if (filter === TIMELINE_FILTERS.SURVEY) return isSurveyMessage(message);
  return false;
};

export const matchesTimelineFilter = (message, filter) => {
  if (!message || filter === TIMELINE_FILTERS.ALL) return true;
  if (filter === TIMELINE_FILTERS.CONVERSATION) return isConversationMessage(message);
  if (filter === TIMELINE_FILTERS.INTERNAL) return isInternalNoteType(message);
  if (filter === TIMELINE_FILTERS.ACTIVITY) return isOperationalActivityLog(message);
  if (filter === TIMELINE_FILTERS.ACCESS) return isLeadAccessLog(message);
  if (filter === TIMELINE_FILTERS.API_LOGS) return isPartnerApiLogMessage(message);
  if (filter === TIMELINE_FILTERS.AI_LOGS) return isAiAgentLogMessage(message);
  if (isTimelineConversationFacet(filter)) return matchesConversationFacet(message, filter);
  return true;
};

/**
 * Semântica inteligente (OR entre camadas; refinamentos estreitam "conversa"):
 * - vazio = tudo visível, exceto acessos LGPD
 * - só facetas (mídia/e-mail/…) = união desses subconjuntos da conversa (+ survey logs)
 * - conversa + facetas = conversa restrita às facetas (AND implícito)
 * - outras camadas (anotações, atividade, acessos, API, IA) entram em OR
 */
export const matchesTimelineFilters = (message, activeFilters) => {
  const filters = Array.isArray(activeFilters) ? activeFilters.filter(Boolean) : [];
  if (!message) return false;
  // Acessos LGPD só aparecem com a camada explícita — inclusive em "Ver tudo".
  if (!filters.length) return !isLeadAccessLog(message);

  const layers = filters.filter((f) => isTimelineLayerFilter(f));
  const facets = filters.filter((f) => isTimelineConversationFacet(f));
  const hasConversation = layers.includes(TIMELINE_FILTERS.CONVERSATION);
  const otherLayers = layers.filter((f) => f !== TIMELINE_FILTERS.CONVERSATION);

  if (otherLayers.some((f) => matchesTimelineFilter(message, f))) return true;

  if (facets.length) {
    const facetHit = facets.some((f) => matchesConversationFacet(message, f));
    if (hasConversation || layers.length === 0) {
      return facetHit;
    }
    // Facetas + outras camadas sem "conversa": facetas ainda valem como atalho da conversa
    if (facetHit) return true;
  }

  if (hasConversation && !facets.length) {
    return isConversationMessage(message);
  }

  return false;
};

/** Presets rápidos do menu (ids estáveis). */
export const TIMELINE_FILTER_PRESETS = {
  EVERYTHING: "preset_everything",
  CUSTOMER_ONLY: "preset_customer_only",
  WORK: "preset_work",
  HIDE_TECHNICAL: "preset_hide_technical",
  DIAGNOSTIC: "preset_diagnostic",
};

export const resolveTimelineFilterPreset = (presetId, { canViewApiAudit = false } = {}) => {
  switch (presetId) {
    case TIMELINE_FILTER_PRESETS.EVERYTHING:
      return [];
    case TIMELINE_FILTER_PRESETS.CUSTOMER_ONLY:
      return [TIMELINE_FILTERS.CONVERSATION];
    case TIMELINE_FILTER_PRESETS.WORK:
      return [
        TIMELINE_FILTERS.CONVERSATION,
        TIMELINE_FILTERS.INTERNAL,
        TIMELINE_FILTERS.ACTIVITY,
      ];
    case TIMELINE_FILTER_PRESETS.HIDE_TECHNICAL:
      return canViewApiAudit
        ? [TIMELINE_FILTERS.CONVERSATION, TIMELINE_FILTERS.INTERNAL, TIMELINE_FILTERS.ACTIVITY]
        : [];
    case TIMELINE_FILTER_PRESETS.DIAGNOSTIC:
      return canViewApiAudit
        ? [TIMELINE_FILTERS.API_LOGS, TIMELINE_FILTERS.AI_LOGS]
        : [];
    default:
      return null;
  }
};

/** Default inteligente: master começa sem logs técnicos; usuário normal vê tudo. */
export const getDefaultTimelineFilters = ({ canViewApiAudit = false } = {}) => {
  if (canViewApiAudit) {
    return [TIMELINE_FILTERS.CONVERSATION, TIMELINE_FILTERS.INTERNAL, TIMELINE_FILTERS.ACTIVITY];
  }
  return [];
};

export const sanitizeTimelineFilters = (filters, { canViewApiAudit = false } = {}) => {
  const list = Array.isArray(filters) ? filters.filter(Boolean) : [];
  const allowed = new Set([
    ...TOGGLEABLE_TIMELINE_FILTERS,
    ...(canViewApiAudit ? MASTER_TIMELINE_FILTERS : []),
  ]);
  return list.filter((f) => allowed.has(f));
};

export const getMessageChannelKey = (message) => {
  const raw = (message?.channelName ?? message?.channel ?? "").toString().trim().toLowerCase();
  return raw || "unknown";
};

const CLIENT_CHANNEL_PATTERNS = [
  "whatsapp",
  "email",
  "e-mail",
  "phone",
  "telefone",
  "sms",
  "messenger",
  "instagram",
  "facebook",
  "comentário",
  "comentarios",
  "app",
  "wordpress",
  "website",
  "site",
  "ecommerce",
  "ads",
  "0800",
  "rcs",
  "chat",
  "portal cliente",
  "xpro cliente",
];

const GENERIC_CHANNEL_ALIASES = {
  whatsapp: "WhatsApp",
  email: "Email",
  phone: "Phone",
  telefone: "Phone",
  sms: "SMS",
  messenger: "Messenger",
  instagram: "Instagram",
  facebook: "Messenger",
  "facebook comentários": "Facebook Comentários",
  "instagram comentários": "Instagram Comentários",
  app: "App",
  wordpress: "Wordpress",
  website: "Website",
  site: "Site",
  ecommerce: "Ecommerce",
  ads: "Ads",
  call: "Phone",
  chat: "Chat",
  rcs: "RCS",
};

const NON_CLIENT_CHANNEL_LABELS = new Set([
  "note",
  "xpro",
  "unknown",
  "desconhecido",
  "customer",
  "sistema",
  "system",
  "imagem",
  "image",
  "video",
  "audio",
  "document",
  "file",
  "location",
  "text",
  "proposal",
  "survey",
  "auto_response",
  "scheduled",
  "log",
]);

const normalizeClientChannelDisplay = (raw, rawChannelType) => {
  const lower = String(raw || "").toLowerCase();
  if (GENERIC_CHANNEL_ALIASES[lower]) return GENERIC_CHANNEL_ALIASES[lower];
  if (lower.startsWith("whatsapp")) return "WhatsApp";
  if (lower.includes("portal cliente") || lower.includes("xpro cliente")) return "Portal do cliente";
  if (lower.includes("e-mail") || lower === "email") return "Email";
  if (lower === "phone" || lower === "telefone") return "Phone";
  if (rawChannelType && GENERIC_CHANNEL_ALIASES[rawChannelType]) {
    return GENERIC_CHANNEL_ALIASES[rawChannelType];
  }
  return raw;
};

const matchesClientCommunicationChannel = (labelLower, rawChannelType) => {
  if (rawChannelType && GENERIC_CHANNEL_ALIASES[rawChannelType]) return true;
  if (!labelLower) return false;
  return CLIENT_CHANNEL_PATTERNS.some((pattern) => labelLower.includes(pattern));
};

/** Chave estável para contagem de canais de comunicação com o cliente (ex.: WhatsApp, telefone). */
export const getClientCommunicationChannelKey = (message) => {
  if (!message || !isConversationMessage(message)) return null;

  const msgType = (message?.type || "").toLowerCase();
  if (msgType === "log") return null;

  const originTipo = Number(message?.originTipo ?? message?.messageChannelTipo);
  if (originTipo === CHANNEL_TYPES.XPRO_CLIENTE) return "portal cliente";
  if (originTipo === CHANNEL_TYPES.SITE_WEBCHAT) return "chat no site";

  const channelName = (message?.channelName ?? "").toString().trim();
  const channel = (message?.channel ?? "").toString().trim();
  const rawChannelType = (message?.rawChannelType ?? message?.channelType ?? "")
    .toString()
    .trim()
    .toLowerCase();

  if (rawChannelType === "note" || rawChannelType === "log") return null;

  const labelSource = channelName || channel;
  if (!labelSource) {
    if (rawChannelType && GENERIC_CHANNEL_ALIASES[rawChannelType]) {
      return `type:${rawChannelType}`;
    }
    return null;
  }

  const lower = labelSource.toLowerCase();
  if (NON_CLIENT_CHANNEL_LABELS.has(lower)) {
    if (rawChannelType && GENERIC_CHANNEL_ALIASES[rawChannelType]) {
      return channelName ? channelName.toLowerCase() : `type:${rawChannelType}`;
    }
    return null;
  }

  if (!matchesClientCommunicationChannel(lower, rawChannelType)) return null;

  return channelName ? channelName.toLowerCase() : `type:${rawChannelType || lower}`;
};

/** Rótulo amigável do canal de comunicação com o cliente. */
export const getClientCommunicationChannelLabel = (message) => {
  const originTipo = Number(message?.originTipo ?? message?.messageChannelTipo);
  if (originTipo === CHANNEL_TYPES.XPRO_CLIENTE) return "Portal do cliente";
  if (originTipo === CHANNEL_TYPES.SITE_WEBCHAT) return "Chat no site";

  if (!getClientCommunicationChannelKey(message)) return null;

  const channelName = (message?.channelName ?? "").toString().trim();
  const channel = (message?.channel ?? "").toString().trim();
  const rawChannelType = (message?.rawChannelType ?? message?.channelType ?? "")
    .toString()
    .trim()
    .toLowerCase();

  if (channelName) return channelName;
  return normalizeClientChannelDisplay(channel, rawChannelType);
};

export const getMessageAuthorUserId = (message) => {
  if (message?.senderId != null && message?.senderId !== "" && Number(message.senderId) !== 0) {
    return String(message.senderId);
  }
  if (message?.id_usuario != null && message?.id_usuario !== "" && Number(message.id_usuario) !== 0) {
    return String(message.id_usuario);
  }
  return null;
};

/** Perspectiva do usuário logado: mensagem/anotação enviada por ele vs. demais. */
export const isMessageFromLoggedInUser = (message, user) => {
  const loggedUserId = user?.id != null ? String(user.id) : null;
  const msgUserId = getMessageAuthorUserId(message);
  return !!loggedUserId && !!msgUserId && msgUserId === loggedUserId;
};
